import React, { useState, useMemo } from "react";
import { UserPlus, Mail, Trash2, RefreshCw, Inbox, CheckCircle, Pencil, Save, X, Upload, FileText, Download, Archive, ArchiveRestore, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useTableControls, SortableTh, SearchBar, applyControls } from "../tableTools";

const SEARCH_FIELDS = [
  { value: "full_name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "town", label: "Town" },
  { value: "status", label: "Status" },
];

const STATUS_FILTERS = [
  { value: "all", label: "All Active" },
  { value: "new", label: "New (Not Welcomed)" },
  { value: "welcomed", label: "Welcomed" },
  { value: "archived", label: "Archived" },
];

const getValue = (a, field) => {
  switch (field) {
    case "full_name": return a.full_name;
    case "email": return a.email;
    case "town": return a.town;
    case "status": return a.status;
    case "created_at": return a.created_at;
    default: return "";
  }
};

const BLANK = { full_name: "", preferred_name: "", email: "", phone: "", town: "", age: "", how_heard: "", experience: "", emergency_contact: "", emergency_relationship: "", emergency_email: "", emergency_phone: "" };

const AddApplicantForm = ({ onAdd, onClose }) => {
  const [f, setF] = useState(BLANK);
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onAdd(f);
    if (ok) { setF(BLANK); onClose(); }
  };
  const field = (key, label, type = "text") => (
    <div>
      <Label className="text-xs text-zinc-400">{label}</Label>
      <Input type={type} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })}
        className="bg-black border-zinc-700 text-white mt-1" data-testid={`applicant-field-${key}`} />
    </div>
  );
  return (
    <form onSubmit={submit} className="p-4 border border-zinc-700 rounded-sm bg-zinc-900/50 grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="add-applicant-form">
      {field("full_name", "Full Name *")}
      {field("preferred_name", "Preferred Name")}
      {field("email", "Email *", "email")}
      {field("phone", "Phone")}
      {field("town", "Town")}
      {field("age", "Age / Status")}
      {field("how_heard", "How Heard")}
      {field("emergency_contact", "Emergency Contact")}
      {field("emergency_relationship", "Emergency Relationship")}
      {field("emergency_email", "Emergency Email", "email")}
      {field("emergency_phone", "Emergency Cell")}
      <div className="md:col-span-2">
        <Label className="text-xs text-zinc-400">Experience</Label>
        <Textarea value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })}
          className="bg-black border-zinc-700 text-white mt-1" rows={2} data-testid="applicant-field-experience" />
      </div>
      <div className="md:col-span-2 flex gap-2">
        <Button type="submit" className="bg-crew-yellow text-black font-bold hover:bg-white" data-testid="submit-applicant-btn">Add Applicant</Button>
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">Cancel</Button>
      </div>
    </form>
  );
};

const ApplicantRow = ({ applicant, templates, onSendWelcome, onDelete, onUpdate, isSelected, onSelect, onArchive, onUnarchive, isArchived }) => {
  const [templateId, setTemplateId] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});

  const startEdit = () => {
    setEdit({
      full_name: applicant.full_name || "",
      preferred_name: applicant.preferred_name || "",
      email: applicant.email || "",
      phone: applicant.phone || "",
      town: applicant.town || "",
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    const ok = await onUpdate(applicant.id, edit);
    if (ok) setEditing(false);
  };
  const ei = (key) => (
    <Input value={edit[key]} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })}
      className="bg-black border-zinc-700 text-white text-sm h-8" data-testid={`edit-${key}-${applicant.id}`} />
  );

  if (editing) {
    return (
      <tr className="border-b border-zinc-800/50 bg-zinc-900/60 align-top" data-testid={`applicant-edit-row-${applicant.id}`}>
        <td className="py-2 px-3">
          <Checkbox checked={isSelected} onCheckedChange={() => onSelect(applicant.id)} disabled className="opacity-50" />
        </td>
        <td className="py-2 px-3">{ei("full_name")}<div className="mt-1">{ei("preferred_name")}</div></td>
        <td className="py-2 px-3">{ei("email")}</td>
        <td className="py-2 px-3">{ei("phone")}</td>
        <td className="py-2 px-3">{ei("town")}</td>
        <td className="py-2 px-3 text-zinc-500 text-xs">{applicant.experience}</td>
        <td className="py-2 px-3" />
        <td className="py-2 px-3">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={saveEdit} className="bg-crew-yellow text-black font-bold hover:bg-white h-8" data-testid={`save-applicant-${applicant.id}`}>
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="text-zinc-400 hover:text-white h-8 w-8 p-0" data-testid={`cancel-applicant-${applicant.id}`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-zinc-800/50 hover:bg-zinc-900/50 align-top" data-testid={`applicant-row-${applicant.id}`}>
      <td className="py-3 px-3">
        <Checkbox 
          checked={isSelected} 
          onCheckedChange={() => onSelect(applicant.id)}
          data-testid={`select-applicant-${applicant.id}`}
        />
      </td>
      <td className="py-3 px-3">
        <div className="font-medium text-white">{applicant.full_name}</div>
        {applicant.preferred_name && <div className="text-xs text-zinc-500">&quot;{applicant.preferred_name}&quot;</div>}
      </td>
      <td className="py-3 px-3 text-zinc-400">{applicant.email}</td>
      <td className="py-3 px-3 text-zinc-400">{applicant.phone}</td>
      <td className="py-3 px-3 text-zinc-400">{applicant.town}</td>
      <td className="py-3 px-3 text-zinc-500 text-xs max-w-[200px] truncate" title={applicant.experience}>{applicant.experience}</td>
      <td className="py-3 px-3">
        {applicant.archived ? (
          <span className="inline-flex items-center text-zinc-500 text-xs"><Archive className="w-3 h-3 mr-1" />Archived</span>
        ) : applicant.welcomed ? (
          <span className="inline-flex items-center text-green-500 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Welcomed</span>
        ) : (
          <span className="text-zinc-500 text-xs">New</span>
        )}
      </td>
      <td className="py-3 px-3">
        <div className="flex items-center gap-2 flex-wrap">
          {!isArchived && (
            <>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="w-[120px] h-8 bg-black border-zinc-700 text-white text-xs" data-testid={`template-select-${applicant.id}`}>
                  <SelectValue placeholder="Template..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {(templates || []).map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-white hover:bg-zinc-800">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!templateId} onClick={() => {
                  if (applicant.welcomed && !window.confirm(`${applicant.full_name} was already welcomed. Send again?`)) return;
                  onSendWelcome(applicant.id, templateId);
                }}
                className="bg-crew-yellow text-black font-bold hover:bg-white h-8" data-testid={`send-welcome-${applicant.id}`}>
                <Mail className="h-3 w-3 mr-1" /> {applicant.welcomed ? "Resend" : "Welcome"}
              </Button>
              <Button size="sm" variant="ghost" onClick={startEdit}
                className="text-blue-400 hover:bg-blue-500 hover:text-white h-8 w-8 p-0" title="Edit" data-testid={`edit-applicant-${applicant.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onArchive(applicant.id)}
                className="text-orange-400 hover:bg-orange-500 hover:text-white h-8 w-8 p-0" title="Archive" data-testid={`archive-applicant-${applicant.id}`}>
                <Archive className="h-4 w-4" />
              </Button>
            </>
          )}
          {isArchived && (
            <Button size="sm" variant="outline" onClick={() => onUnarchive(applicant.id)}
              className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white h-8" data-testid={`unarchive-applicant-${applicant.id}`}>
              <ArchiveRestore className="h-4 w-4 mr-1" /> Restore
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(applicant.id)}
            className="text-red-500 hover:bg-red-500 hover:text-white h-8 w-8 p-0" data-testid={`delete-applicant-${applicant.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const ApplicantsTab = (props) => {
  const { 
    applicants,
    archivedApplicants,
    emailTemplates, 
    handleAddApplicant, 
    handleDeleteApplicant, 
    handleUpdateApplicant, 
    handleSendWelcome, 
    handleScanApplications, 
    handleUploadApplicantsCSV,
    handleBulkWelcome,
    handleExportApplicantsCSV,
    handleArchiveApplicant,
    handleUnarchiveApplicant,
    handleAutoArchive,
    archiveStats,
    onOpenCSVImport
  } = props;
  
  const [showAdd, setShowAdd] = useState(false);
  const [showCsvInfo, setShowCsvInfo] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTemplateId, setBulkTemplateId] = useState("");
  const ctrl = useTableControls("created_at", "full_name");

  // Determine which list to show based on filter
  const isShowingArchived = statusFilter === "archived";
  const sourceList = isShowingArchived ? (archivedApplicants || []) : applicants;

  // Filter by status first
  const filteredByStatus = useMemo(() => {
    if (statusFilter === "all") return sourceList.filter(a => !a.archived);
    if (statusFilter === "new") return sourceList.filter(a => !a.welcomed && !a.archived);
    if (statusFilter === "welcomed") return sourceList.filter(a => a.welcomed && !a.archived);
    if (statusFilter === "archived") return sourceList;
    return sourceList;
  }, [sourceList, statusFilter]);

  // Then apply search/sort controls
  const rows = applyControls(filteredByStatus, ctrl, getValue);

  // Stats from archiveStats or calculated
  const newCount = archiveStats?.new ?? applicants.filter(a => !a.welcomed && !a.archived).length;
  const welcomedCount = archiveStats?.welcomed ?? applicants.filter(a => a.welcomed && !a.archived).length;
  const archivedCount = archiveStats?.archived ?? (archivedApplicants || []).length;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map(r => r.id)));
    }
  };

  const handleBulkWelcomeClick = async () => {
    if (selectedIds.size === 0) return;
    if (!bulkTemplateId) {
      alert("Please select a template first");
      return;
    }
    const ids = Array.from(selectedIds);
    const unwelcomed = ids.filter(id => {
      const app = applicants.find(a => a.id === id);
      return app && !app.welcomed;
    });
    
    if (unwelcomed.length === 0) {
      if (!window.confirm("All selected applicants have already been welcomed. Send again?")) return;
    }
    
    if (handleBulkWelcome) {
      const result = await handleBulkWelcome(ids, bulkTemplateId);
      if (result) {
        setSelectedIds(new Set());
      }
    }
  };

  const selectedUnwelcomedCount = Array.from(selectedIds).filter(id => {
    const app = applicants.find(a => a.id === id);
    return app && !app.welcomed;
  }).length;

  return (
    <TabsContent value="applicants" data-testid="applicants-content">
      <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <UserPlus className="w-6 h-6 text-crew-yellow" />
              <h2 className="text-2xl font-bold text-crew-yellow">APPLICANTS</h2>
            </div>
            <p className="text-zinc-400 text-sm mt-1">
              {applicants.length + archivedCount} total • <span className="text-blue-400">{newCount} new</span> • <span className="text-green-400">{welcomedCount} welcomed</span> • <span className="text-zinc-500">{archivedCount} archived</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SearchBar fields={SEARCH_FIELDS} testid="applicant-search" {...ctrl} />
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setSelectedIds(new Set()); }}>
              <SelectTrigger className="w-[150px] h-9 bg-black border-zinc-700 text-white" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                {STATUS_FILTERS.map(sf => (
                  <SelectItem key={sf.value} value={sf.value} className="text-white hover:bg-zinc-800">
                    {sf.label}{sf.value === "archived" && archivedCount > 0 ? ` (${archivedCount})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!isShowingArchived && (
              <>
                <Button onClick={handleScanApplications} variant="outline"
                  className="border-crew-yellow text-crew-yellow hover:bg-crew-yellow hover:text-black" data-testid="scan-applications-btn">
                  <Inbox className="mr-2 h-4 w-4" /> Scan Inbox
                </Button>
                
                {/* CSV Import Button - Opens Dialog with Field Mapping */}
                <Button 
                  onClick={onOpenCSVImport} 
                  variant="outline"
                  className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white" 
                  data-testid="upload-csv-btn"
                >
                  <Upload className="mr-2 h-4 w-4" /> Import CSV
                </Button>
              </>
            )}

            {/* Export CSV Button */}
            <Button 
              onClick={handleExportApplicantsCSV} 
              variant="outline"
              className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white" 
              data-testid="export-csv-btn"
              disabled={applicants.length === 0 && archivedCount === 0}
            >
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>

            {/* Auto Archive Button */}
            {!isShowingArchived && (
              <Button 
                onClick={handleAutoArchive} 
                variant="outline"
                className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white" 
                data-testid="auto-archive-btn"
                title="Archive welcomed applicants older than 30 days"
              >
                <Clock className="mr-2 h-4 w-4" /> Auto-Archive
              </Button>
            )}

            {!isShowingArchived && (
              <Button onClick={() => setShowAdd((v) => !v)} className="bg-crew-yellow text-black font-bold hover:bg-white" data-testid="add-applicant-btn">
                <UserPlus className="mr-2 h-4 w-4" /> Add Applicant
              </Button>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* Bulk Actions Bar */}
          {selectedIds.size > 0 && (
            <div className="mb-4 p-3 bg-zinc-900/80 border border-zinc-700 rounded-sm flex flex-wrap items-center gap-3" data-testid="bulk-actions-bar">
              <span className="text-white font-medium">
                {selectedIds.size} selected
                {selectedUnwelcomedCount > 0 && selectedUnwelcomedCount < selectedIds.size && (
                  <span className="text-zinc-400 text-sm ml-2">({selectedUnwelcomedCount} not yet welcomed)</span>
                )}
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <Select value={bulkTemplateId} onValueChange={setBulkTemplateId}>
                  <SelectTrigger className="w-[150px] h-8 bg-black border-zinc-700 text-white text-sm" data-testid="bulk-template-select">
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    {(emailTemplates || []).map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-white hover:bg-zinc-800">{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  size="sm" 
                  onClick={handleBulkWelcomeClick}
                  disabled={!bulkTemplateId}
                  className="bg-crew-yellow text-black font-bold hover:bg-white"
                  data-testid="bulk-welcome-btn"
                >
                  <Mail className="h-4 w-4 mr-2" /> Send Welcome to Selected
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-zinc-400 hover:text-white"
                  data-testid="clear-selection-btn"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* CSV Format Info Panel */}
          <div className="mb-4">
            <button
              onClick={() => setShowCsvInfo(!showCsvInfo)}
              className="flex items-center gap-2 text-sm text-zinc-400 hover:text-crew-yellow transition-colors"
              data-testid="csv-info-toggle"
            >
              <FileText className="w-4 h-4" />
              <span>CSV Import Format Guide</span>
              <span className="text-xs">{showCsvInfo ? '▲' : '▼'}</span>
            </button>
            {showCsvInfo && (
              <div className="mt-3 p-4 bg-zinc-900/50 border border-zinc-700 rounded-sm text-sm" data-testid="csv-info-panel">
                <h4 className="text-crew-yellow font-bold mb-2">CSV Column Format</h4>
                <div className="grid md:grid-cols-2 gap-4 text-zinc-300">
                  <div>
                    <p className="font-semibold text-white mb-1">Required Columns:</p>
                    <ul className="list-disc list-inside text-zinc-400 space-y-1">
                      <li><code className="text-green-400">full_name</code> (or: name, applicant_name)</li>
                      <li><code className="text-green-400">email</code> (or: email_address)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Optional Columns:</p>
                    <ul className="list-disc list-inside text-zinc-400 space-y-1">
                      <li><code className="text-blue-400">preferred_name</code> (or: nickname, goes_by)</li>
                      <li><code className="text-blue-400">phone</code> (or: phone_number, cell, mobile)</li>
                      <li><code className="text-blue-400">town</code> (or: city, location)</li>
                      <li><code className="text-blue-400">age</code> (or: status, age_group)</li>
                      <li><code className="text-blue-400">how_heard</code> (or: referral, source)</li>
                      <li><code className="text-blue-400">experience</code> (or: skills, background)</li>
                      <li><code className="text-blue-400">emergency_contact</code></li>
                    </ul>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-black/50 rounded text-xs font-mono text-zinc-400 overflow-x-auto">
                  <p className="text-zinc-500 mb-1"># Example CSV:</p>
                  <code>full_name,email,preferred_name,phone,town,age,experience</code><br/>
                  <code>John Smith,john@email.com,Johnny,555-1234,Westport,Working,AV experience</code>
                </div>
                <p className="mt-2 text-zinc-500 text-xs">
                  Note: Duplicate emails are automatically skipped. Column names are case-insensitive.
                </p>
              </div>
            )}
          </div>

          {showAdd && !isShowingArchived && <AddApplicantForm onAdd={handleAddApplicant} onClose={() => setShowAdd(false)} />}

          {rows.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="no-applicants">
              <RefreshCw className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>
                {isShowingArchived 
                  ? "No archived applicants." 
                  : applicants.length === 0 
                    ? 'No applicants yet. Click "Scan Inbox" to pull forwarded applications, or "Add Applicant" to enter one manually.' 
                    : "No matching applicants."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-zinc-400 border-b border-zinc-800 bg-zinc-900">
                  <tr>
                    <th className="py-3 px-3 w-10">
                      {!isShowingArchived && (
                        <Checkbox 
                          checked={selectedIds.size === rows.length && rows.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      )}
                    </th>
                    <SortableTh field="full_name" label="Name" {...ctrl} onSort={ctrl.handleSort} />
                    <SortableTh field="email" label="Email" {...ctrl} onSort={ctrl.handleSort} />
                    <th className="py-3 px-3">Phone</th>
                    <SortableTh field="town" label="Town" {...ctrl} onSort={ctrl.handleSort} />
                    <th className="py-3 px-3">Experience</th>
                    <SortableTh field="status" label="Status" {...ctrl} onSort={ctrl.handleSort} />
                    <th className="py-3 px-3">{isShowingArchived ? "Actions" : "Send Welcome"}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <ApplicantRow 
                      key={a.id} 
                      applicant={a} 
                      templates={emailTemplates}
                      onSendWelcome={handleSendWelcome} 
                      onDelete={handleDeleteApplicant} 
                      onUpdate={handleUpdateApplicant}
                      isSelected={selectedIds.has(a.id)}
                      onSelect={toggleSelect}
                      onArchive={handleArchiveApplicant}
                      onUnarchive={handleUnarchiveApplicant}
                      isArchived={isShowingArchived}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
};
